import type { Me } from './TeamView';

/** A submitter the admin can preview as — the pair the picker hands around. */
export interface PreviewTarget {
  submitterId: string;
  submitterAlias: string;
}

/**
 * AOZONE-115. Synthesizes the identity that makes TeamView render as a given
 * submitter, so the contributor view's EXISTING own/all logic runs unchanged
 * rather than being special-cased for preview mode.
 *
 * The invariant that carries the whole feature: a synthesized contributor
 * identity always carries a non-empty `slackUserId`. TeamView's split is
 * `restrictToOwn = !showAll && !!mySlackId` (TeamView.tsx:86), so a contributor
 * identity WITHOUT one renders the entire team's rows beneath a "Showing only
 * your entries" caption with the box unchecked — silently wrong in exactly the
 * logic most likely to hide the next bug. That is why an empty id throws rather
 * than degrading: the fallback IS the bug.
 *
 * Because it throws, and because this SPA has no ErrorBoundary, callers must
 * build the identity in a SELECTION HANDLER and hold the result in state — not
 * in a render body, where a throw would blank the page. App.tsx does this in
 * `enterPreview`. `PreviewContributorBar` additionally drops blank-id rows from
 * the picker, so the refusal is unreachable from the UI; those two guards are a
 * pair, and neither should be removed without the other.
 *
 * This is a RENDERING identity, never an authorization one. It exists only in
 * the previewing admin's React state; their cookie remains an admin cookie and
 * every privileged route stays privileged. Do not read it as an auth downgrade.
 */
export function simulatedContributorMe(slackUserId: string, email?: string): Me {
  if (slackUserId.trim() === '') {
    throw new Error('simulatedContributorMe: refusing to synthesize a contributor with no slackUserId');
  }
  return { role: 'contributor', slackUserId, ...(email ? { email } : {}) };
}
